import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { StaffCreateResponse, StaffDTO, StaffRole } from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { useAuth } from '../../providers/AuthProvider';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';
import { Dialog } from '../../components/a11y/Dialog';

/**
 * Staff — OWNER-ONLY management of the admin login accounts (owner + manager/staff). A created
 * account gets a one-time temp password (shown once, to relay securely); the new person signs in
 * with it, then sets their own password + enrolls MFA. Suspending signs them out immediately. An
 * owner can't suspend/demote their own account, and the last active owner can't be removed (server
 * enforces both). Tenants are managed on the Tenants page, never here.
 */

const STAFF_KEY = ['admin', 'staff'] as const;

function CreateStaffForm({
  onCreated,
  onClose,
}: {
  onCreated: (r: StaffCreateResponse) => void;
  onClose: () => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<StaffRole>('staff');

  const save = useMutation({
    mutationFn: () => api.staff.create({ email: email.trim(), name: name.trim(), role }),
    onSuccess: (res) => onCreated(res),
  });

  return (
    <Dialog title="New account" onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <StatusMessage tone="err">{errorMessage(save.error)}</StatusMessage>}
        <div>
          <label htmlFor="s-name">Name</label>
          <input id="s-name" value={name} required maxLength={200} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label htmlFor="s-email">Email</label>
          <input
            id="s-email"
            type="email"
            autoComplete="off"
            value={email}
            required
            maxLength={200}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <fieldset style={{ border: '1px solid var(--border)', borderRadius: 8 }}>
          <legend>Role</legend>
          <div className="row">
            <input
              id="role-staff"
              type="radio"
              name="role"
              style={{ width: 'auto' }}
              checked={role === 'staff'}
              onChange={() => setRole('staff')}
            />
            <label htmlFor="role-staff" style={{ margin: 0 }}>
              Manager (staff)
            </label>
            <input
              id="role-owner"
              type="radio"
              name="role"
              style={{ width: 'auto' }}
              checked={role === 'owner'}
              onChange={() => setRole('owner')}
            />
            <label htmlFor="role-owner" style={{ margin: 0 }}>
              Owner (full access)
            </label>
          </div>
        </fieldset>
        <div className="row">
          <button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Creating…' : 'Create account'}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function TempPasswordDialog({ created, onClose }: { created: StaffCreateResponse; onClose: () => void }) {
  return (
    <Dialog title="Account created" onClose={onClose}>
      <div className="stack">
        <StatusMessage tone="ok">Account for {created.staff.email} created.</StatusMessage>
        <p>
          Share this <strong>temporary password</strong> with them securely (in person or a channel
          other than email). On first sign-in they'll set their own password and enroll two-factor
          authentication.
        </p>
        <p className="notice-callout">
          <code style={{ fontSize: '1.1rem', userSelect: 'all' }}>{created.tempPassword}</code>
        </p>
        <p className="field-hint">Shown only once — copy it now.</p>
        <button type="button" onClick={onClose}>
          Done
        </button>
      </div>
    </Dialog>
  );
}

export function AdminStaff() {
  const queryClient = useQueryClient();
  const { principal } = useAuth();
  const query = useQuery({ queryKey: STAFF_KEY, queryFn: () => api.staff.list() });
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<StaffCreateResponse | null>(null);

  const update = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'suspended' }) =>
      api.staff.update(id, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: STAFF_KEY }),
  });

  if (query.isLoading) return <Loading label="Loading staff…" />;
  if (query.isError || !query.data)
    return <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} />;

  const staff = query.data.staff;
  const statusLabel = (s: StaffDTO['status']) =>
    s === 'active' ? 'Active' : s === 'suspended' ? 'Suspended' : 'Invited';

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1>Staff</h1>
        <button type="button" onClick={() => setCreating(true)}>
          New account
        </button>
      </div>
      <p className="subtitle">
        Owner and manager logins for this admin portal. New accounts sign in with a one-time password,
        then set their own and enroll two-factor.
      </p>

      {update.isError && <StatusMessage tone="err">{errorMessage(update.error)}</StatusMessage>}

      <div className="table-wrap card" style={{ padding: 0 }}>
        <table>
          <caption className="visually-hidden">Staff accounts</caption>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Email</th>
              <th scope="col">Role</th>
              <th scope="col">Status</th>
              <th scope="col">2FA</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {staff.map((s) => {
              const isSelf = s.id === principal?.userId;
              return (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td>{s.email}</td>
                  <td>{s.role === 'owner' ? 'Owner' : 'Manager'}</td>
                  <td>{statusLabel(s.status)}</td>
                  <td>{s.mfaEnrolled ? 'Enrolled' : '—'}</td>
                  <td>
                    <Link className="button-link" to={`/admin/activity/staff/${encodeURIComponent(s.id)}`}>
                      Activity<span className="visually-hidden"> for {s.name}</span>
                    </Link>{' '}
                    {isSelf ? (
                      <span className="muted">You</span>
                    ) : s.status === 'suspended' ? (
                      <button
                        type="button"
                        className="secondary"
                        disabled={update.isPending}
                        onClick={() => update.mutate({ id: s.id, status: 'active' })}
                      >
                        Reactivate<span className="visually-hidden"> {s.name}</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="secondary"
                        disabled={update.isPending}
                        onClick={() => {
                          if (window.confirm(`Suspend ${s.name}? They'll be signed out and unable to log in.`))
                            update.mutate({ id: s.id, status: 'suspended' });
                        }}
                      >
                        Suspend<span className="visually-hidden"> {s.name}</span>
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {creating && (
        <CreateStaffForm
          onClose={() => setCreating(false)}
          onCreated={(r) => {
            setCreating(false);
            setCreated(r);
            void queryClient.invalidateQueries({ queryKey: STAFF_KEY });
          }}
        />
      )}
      {created && <TempPasswordDialog created={created} onClose={() => setCreated(null)} />}
    </>
  );
}
